import React, { useState } from "react";
import { Link } from "react-router-dom";

import Seo from "../seo/Seo";
import {
  organizationSchema,
  websiteSchema,
  webPageSchema,
  faqSchema,
  appSchema,
} from "../seo/schema";

import PageHeader from "../components/PageHeader";
import LiveResultsTable from "../components/LiveResultsTable";
import MonthlyChartTable from "../components/MonthlyChartTable";
import MonthYearFilter from "../components/MonthYearFilter";
import AppPromo from "../components/AppPromo";

import { GAMES_BY_TIME } from "../data/games";
import { APP_URL } from "../data/site";
import { useLiveResults, useNowIST, useChartResults } from "../lib/useResults";
import { formatISTDate, formatMonthYear, pad2 } from "../lib/results";

const HOME_FAQS = [
  {
    q: "What time is the Bagad satta result declared today?",
    a: "The Bagad result is declared at 1:30 PM IST every day. The number appears on this page automatically within moments of being declared — you do not need to refresh.",
  },
  {
    q: "Which satta results can I check on Bagad Satta Company?",
    a: "Ten games: Disawar (5:20 AM), Bagad (1:30 PM), Delhi Bazar (3:15 PM), Shri Ganesh (4:45 PM), Faridabad (6:15 PM), Jai Bhawani (7:15 PM), Ghaziabad (9:30 PM), Jai Hanuman (10:20 PM), Gogamedi (11:30 PM) and Gali (11:30 PM). All times are IST.",
  },
  {
    q: "How do I see old satta results and monthly charts?",
    a: "Open the Satta Chart page and pick any month and year from 2024 onwards. Every declared number for that month is shown in a single day-by-day grid.",
  },
  {
    q: "Why does a result show XX instead of a number?",
    a: "XX means the result for that day has not been declared yet, or no number was published for that date. The number replaces XX automatically once it is announced.",
  },
  {
    q: "What is the difference between a PRO and a MAX account?",
    a: "A PRO account pays a 10 → 950 jodi and haruf rate with 1% commission on every deposit. A MAX account pays a 10 → 900 rate with 5% commission on every deposit. Both start from a minimum deposit of ₹30.",
  },
  {
    q: "Is Bagad Satta Company free to use?",
    a: "Yes. Viewing live results and every historical chart on this website is completely free, with no registration required.",
  },
];

const Home = () => {
  const nowIST = useNowIST();
  const { rows, isLoading, error } = useLiveResults();

  const currentYear = nowIST.getFullYear();
  const currentMonthIndex = nowIST.getMonth();

  const [selectedMonthIndex, setSelectedMonthIndex] =
    useState(currentMonthIndex);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const { chartResults, isLoading: chartLoading } =
    useChartResults(selectedYear);

  const todayStr = formatISTDate(nowIST);
  const todayISO = nowIST.toISOString();

  // Declared numbers go straight into the meta description, so the snippet
  // Google shows carries today's actual result instead of boilerplate.
  const declared = rows
    .filter((row) => row.today != null && row.shouldShowToday)
    .map((row) => `${row.game.name} ${pad2(row.today)}`);

  const description = declared.length
    ? `Satta result today ${todayStr}: ${declared
        .slice(0, 6)
        .join(", ")}. Live Bagad, Disawar, Gali, Faridabad and Ghaziabad results with full monthly charts.`
    : `Live satta result today (${todayStr}) for Bagad, Disawar, Delhi Bazar, Faridabad, Ghaziabad, Gali, Gogamedi, Jai Bhawani, Jai Hanuman and Shri Ganesh, plus monthly record charts from 2024 onwards.`;

  const title = `Satta Result Today ${todayStr} | Bagad Satta Company Live Chart`;

  return (
    <>
      <Seo
        path="/"
        title={title}
        description={description}
        keywords="satta result today, bagad satta result, bagad satta company, disawar result, gali result, faridabad satta result, ghaziabad satta result, gogamedi result, jai bhawani result, jai hanuman result, satta chart"
        modifiedTime={todayISO}
        schema={[
          organizationSchema(),
          websiteSchema(),
          webPageSchema({
            path: "/",
            title,
            description,
            modifiedTime: todayISO,
          }),
          faqSchema(HOME_FAQS),
          appSchema(APP_URL),
        ]}
      />

      <div className="page-banner">
        <div className="container page-padding">
          <PageHeader
            h1={`Satta Result Today — ${todayStr}`}
            intro="Live results and complete monthly charts for all ten Bagad Satta Company games, updated the moment each number is declared."
            nowIST={nowIST}
            showClock
          />

          <section className="card" aria-labelledby="live-results-title">
            <h2 className="card-title" id="live-results-title">
              Live Satta Result — {todayStr}
            </h2>
            <p className="card-lede">
              Yesterday&apos;s and today&apos;s declared numbers for every game.
              Tap a game name for its full history, timing and record chart.
            </p>
            <LiveResultsTable
              rows={rows}
              isLoading={isLoading}
              error={error}
            />
          </section>

          <AppPromo />

          <section className="card monthly-card" aria-labelledby="chart-title">
            <h2 className="card-title card-h3" id="chart-title">
              Satta Chart — {formatMonthYear(selectedMonthIndex, selectedYear)}
            </h2>

            <MonthYearFilter
              selectedYear={selectedYear}
              selectedMonthIndex={selectedMonthIndex}
              currentYear={currentYear}
              currentMonthIndex={currentMonthIndex}
              onYearChange={(yr) => {
                setSelectedYear(yr);
                if (yr === currentYear && selectedMonthIndex > currentMonthIndex) {
                  setSelectedMonthIndex(currentMonthIndex);
                }
              }}
              onMonthChange={setSelectedMonthIndex}
              idPrefix="home"
            />

            <MonthlyChartTable
              games={GAMES_BY_TIME}
              chartResults={chartResults}
              selectedMonthIndex={selectedMonthIndex}
              selectedYear={selectedYear}
              isLoading={chartLoading}
            />

            <p className="card-footnote">
              Looking for an older month? The{" "}
              <Link to="/satta-chart">full satta chart</Link> covers every month
              from 2024 to {currentYear}.
            </p>
          </section>

          <section className="card text-content">
            <h2>Satta Result Timings — All Games</h2>
            <p>
              Every game on Bagad Satta Company declares its number at a fixed
              time each day. The table below is the quickest way to know when to
              check back.
            </p>

            <div className="table-wrap">
              <table className="results-table timing-table">
                <thead>
                  <tr>
                    <th scope="col">Game</th>
                    <th scope="col">Result Time (IST)</th>
                    <th scope="col">Chart</th>
                  </tr>
                </thead>
                <tbody>
                  {GAMES_BY_TIME.map((game, idx) => (
                    <tr
                      key={game.key}
                      className={idx % 2 === 0 ? "row-odd" : "row-even"}
                    >
                      <th scope="row" className="game-left">
                        {game.name}
                      </th>
                      <td>{game.timeLabel}</td>
                      <td>
                        <Link to={`/${game.slug}`}>
                          {game.name} chart
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2>About Bagad Satta Company</h2>
            <p>
              Bagad Satta Company publishes the day&apos;s declared numbers for
              ten games and keeps a permanent record of every past result. The
              live table at the top of this page updates on its own as each
              game&apos;s scheduled time passes, so the number you see is the
              number that was declared — never a placeholder or a guess.
            </p>
            <p>
              Below the live table, the monthly chart shows an entire month at
              once, one row per date and one column per game. Use the year and
              month selectors to move back through {currentYear}, 2025 and 2024.
            </p>

            <h2>Frequently Asked Questions</h2>
            <dl className="faq-list">
              {HOME_FAQS.map((faq) => (
                <div className="faq-item" key={faq.q}>
                  <dt>{faq.q}</dt>
                  <dd>{faq.a}</dd>
                </div>
              ))}
            </dl>
            <p>
              More answers are on the <Link to="/faq">FAQ page</Link>.
            </p>

            <h2>Disclaimer</h2>
            <p>
              This website is published for informational purposes only. We are
              not associated with any satta matka company or gambling
              organisation. Satta and gambling are illegal in many Indian states
              and in many countries. Please follow the law in your region. We do
              not encourage or promote gambling in any form. Read the{" "}
              <Link to="/disclaimer">full disclaimer</Link>.
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

export default Home;
